import { CsbHelper, getConfig, globalTrackingService, loadingHelper } from '@recruiting-solutions/rs-shared';
import { AuthService } from './auth.service';

export class SigninDialogService {
    static linkSigninEmail: string;

    static async openLoginWithProviders(locale: string) {
        await SigninDialogService.open({ showProviders: true }, locale);
    }

    static registerProviderListener(el: ShadowRoot | Document, locale: string) {
        el.addEventListener('click', async (ev: Event) => {
            let target = ev.target as HTMLElement;

            if (target.closest('.do-login-google')) {
                await SigninDialogService.providerSignin('google', locale);
            } else if (target.closest('.do-login-linkedin')) {
                await SigninDialogService.providerSignin('linkedin', locale);
            } else if (target.closest('.do-login-microsoft')) {
                await SigninDialogService.providerSignin('microsoft', locale);
            } else if (target.closest('.do-login-apple')) {
                await SigninDialogService.providerSignin('apple', locale);
            } else if (target.closest('.do-signout')) {
                await AuthService.logout();
            } else if (target.closest('.do-login-with-providers')) {
                SigninDialogService.openLoginWithProviders(locale);
            } else {
                return; // Dont prevent default and stop propagation
            }

            ev.preventDefault();
            ev.stopPropagation();
        });
    }

    private static globalProviderListener = false;
    static registerGlobalProviderListener(locale: string) {
        if (SigninDialogService.globalProviderListener) return;
        SigninDialogService.registerProviderListener(document, locale);
        SigninDialogService.globalProviderListener = true;
    }

    static async open(context = {}, locale: string, id = 'global-login-dialog'): Promise<void> {
        SigninDialogService.registerGlobalProviderListener(locale);

        let dialog: any = document.getElementById(id);
        await dialog.open(async (success: boolean, context: any) => {
            if (success) {
                SigninDialogService.toggleLoadingDialog(true);
                let url = SigninDialogService.linkSigninEmail;
                await AuthService.passwordLessLogin(context.email, url);
                SigninDialogService.toggleLoadingDialog(false);
                window.location.href = dialog.getAttribute('login-redirect-url');
            }
        }, context);

        dialog.enableSuccessButton = await dialog.isValid(false);

        dialog.addEventListener('enterPressed', async () => {
            dialog.trySuccess();
        });

        dialog.addEventListener('changed', function (ev: any) {
            let { valid } = ev.detail;
            dialog.enableSuccessButton = valid;
        });
    }

    static toggleLoadingDialog(show: boolean, id = 'global-login-loading-dialog') {
        let dialog: any = document.getElementById(id);
        if (show) {
            dialog.open();
        } else {
            dialog.close(true);
        }
    }

    static getAccountExistsMessage(locale: string) {
        switch (locale) {
            case 'de_DE':
                return 'Es gibt bereits ein Konto mit der E-Mail Adresse {email}. Bitte melden Sie sich über E-Mail an statt LinkedIn.';
            case 'de_EN':
                return 'There is already an account with the email address (email). Please sign in via email instead of LinkedIn.';
            case 'de_AT':
                return 'Es gibt bereits ein Konto mit der E-Mail Adresse {email}. Bitte melden Sie sich über E-Mail an statt LinkedIn.';
            case 'ch_DE':
                return 'Es gibt bereits ein Konto mit der E-Mail Adresse {email}. Bitte melden Sie sich über E-Mail an statt LinkedIn.';
            case 'ch_FR':
                return `Il existe déjà un compte avec l'adresse e-mail {email}. Veuillez vous connecter par e-mail au lieu de LinkedIn.`;
            case 'ch_IT':
                return `Esiste già un account con l'indirizzo e-mail {email}. Effettuare l'accesso tramite e-mail anziché tramite LinkedIn.`;
            case 'hu_HU':
                return 'Már van egy fiók a {email} e-mail címmel. Kérjük, a LinkedIn helyett e-mailen keresztül jelentkezzen be.';
            case 'it_IT':
                return `Esiste già un account con l'indirizzo e-mail {email}. Effettuare l'accesso tramite e-mail anziché tramite LinkedIn.`;
            case 'it_EN':
                return 'There is already an account with the email address {email}. Please sign in via email instead of LinkedIn.';
            case 'pt_EN':
                return 'There is already an account with the email address {email}. Please sign in via email instead of LinkedIn.';
            case 'pt_PT':
                return 'Já existe uma conta com o endereço de correio eletrónico {email}. Inicie sessão através do correio eletrónico em vez do LinkedIn.';
            case 'pl_PL':
                return 'Istnieje już konto o adresie e-mail {email}. Zaloguj się przez e-mail zamiast LinkedIn.';
            case 'es_ES':
                return `Ya existe una cuenta con la dirección de correo electrónico {email}. Por favor, inicia sesión a través del correo electrónico en lugar de LinkedIn.`;
            case 'es_EN':
                return 'There is already an account with the email address {email}. Please sign in via email instead of LinkedIn.';
            case 'tr_TR':
                return '{email} e-posta adresiyle zaten bir hesap var. Lütfen LinkedIn yerine e-posta ile oturum açın.';
            case 'tr_EN':
                return 'There is already an account with the email address {email}. Please sign in via email instead of LinkedIn.';
            case 'be_FR':
                return `Il existe déjà un compte avec l'adresse e-mail {email}. Veuillez vous connecter par e-mail au lieu de LinkedIn.`;
            case 'be_NL':
                return 'Er is al een account met het e-mailadres {email}. Meld u aan via e-mail in plaats van LinkedIn.';
            case 'nl_NL':
                return 'Er is al een account met het e-mailadres {email}. Meld u aan via e-mail in plaats van LinkedIn.';
            case 'en_US':
            default:
                return 'There is already an account with the email address (email). Please sign in via email instead of LinkedIn.';
        }
    }

    static getLoginButtonText(locale: string) {
        switch (locale) {
            case 'de_DE':
                return 'Mit E-Mail anmelden';
            case 'de_EN':
                return 'Sign in with email';
            case 'de_AT':
                return 'Mit E-Mail anmelden';
            case 'ch_DE':
                return 'Mit E-Mail anmelden';
            case 'ch_FR':
                return `S'inscrire avec un e-mail`;
            case 'ch_IT':
                return `Accedi con l'e-mail`;
            case 'hu_HU':
                return 'Bejelentkezés e-mail címmel';
            case 'it_IT':
                return `Accedi con l'e-mail`;
            case 'it_EN':
                return 'Sign in with email';
            case 'pt_EN':
                return 'Sign in with email';
            case 'pt_PT':
                return 'Iniciar sessão com o e-mail';
            case 'pl_PL':
                return 'Zaloguj się za pomocą adresu e-mail';
            case 'es_ES':
                return 'Iniciar sesión con correo electrónico';
            case 'es_EN':
                return 'Sign in with email';
            case 'tr_TR':
                return 'E-posta ile giriş yapın';
            case 'tr_EN':
                return 'Sign in with email';
            case 'be_FR':
                return `S'inscrire avec un e-mail`;
            case 'be_NL':
                return 'Aanmelden met e-mail';
            case 'nl_NL':
                return 'Aanmelden met e-mail';
            case 'en_US':
            default:
                return 'Sign in with email';
        }
    }

    static getAlreadyLinkedMessage(locale: string) {
        switch (locale) {
            case 'de_DE':
                return 'Es gibt bereits einen Nutzer mit diesem Konto, daher kann es nicht verknüpft werden.';
            case 'en_US':
            default:
                return 'There is already a user with this account, so it cannot be linked.';
        }
    }

    static replacePlaceholders(msg: string, email: string, provider: string) {
        return msg.replace(/{email}/g, email).replace(/{provider}/g, provider);
    }

    private static getLoadingIfNeeded(showLoading?: boolean) {
        if (showLoading) {
            return loadingHelper.setLoading('login');
        }
        return null;
    }

    static async providerSignin(provider: string, locale: string, showLoading?: boolean) {
        const loading = this.getLoadingIfNeeded(showLoading);

        try {
            await AuthService.loginWithProvider(provider);
        } catch (error) {
            let msg: string = '';
            let successCallback: (success: boolean) => void;
            let successLabel: string;
            const email = error.customData?.email;

            if (error.code === 'auth/account-exists-with-different-credential') {
                let existingProvider = await AuthService.getFirstExistingProvider(email);
                if (existingProvider === 'password') {
                    existingProvider = 'email';
                }
                msg = SigninDialogService.getAccountExistsMessage(locale);
                msg = SigninDialogService.replacePlaceholders(msg, email, existingProvider);
                successCallback = (success: boolean) => {
                    if (success) {
                        if (existingProvider === 'email') {
                            let url = SigninDialogService.linkSigninEmail;
                            AuthService.passwordLessLogin(email, url).then(() => {
                                let redirectUrl = (getConfig().mailLoginRedirectUrl || '').replace('{brand}', CsbHelper.getBrandLocal());
                                window.location.href = redirectUrl;
                            });
                        } else {
                            SigninDialogService.providerSignin(existingProvider, locale, showLoading);
                        }
                    }
                };
                successLabel = this.getLoginButtonText(locale);
                successLabel = this.replacePlaceholders(successLabel, email, existingProvider);
            }

            if (error.code === 'auth/credential-already-in-use' || error.code === 'auth/email-already-in-use' || error.code === 'auth/provider-already-linked') {
                msg = SigninDialogService.getAlreadyLinkedMessage(locale);
                msg = SigninDialogService.replacePlaceholders(msg, email, provider);
            }

            if (msg) {
                let dialog = document.getElementById('dialog-signin-error') as any;
                if (dialog) {
                    dialog.open(msg);
                } else {
                    const dialogElement = document.createElement('csb-dialog-info') as any;

                    document.body.appendChild(dialogElement);

                    setTimeout(() => {
                        if (dialogElement) {
                            if (error.code === 'auth/account-exists-with-different-credential') {
                                dialogElement.showSuccessButton = true;
                                dialogElement.labelSuccess = successLabel;
                                dialogElement.open(msg, successCallback);
                            } else {
                                dialogElement.open(msg);
                            }
                        }
                    }, 200);
                }
            }

            globalTrackingService.trackEvent(`Login ${provider} abort`, 'login_abort', { event_category: 'login' });
        } finally {
            loading?.off();
        }
    }

    static async emailSignin(email: string, emailLoginUrl: string, redirectUrl: string, showLoading?: boolean, useLocalStorage?: boolean) {
        const loading = this.getLoadingIfNeeded(showLoading);
        await AuthService.passwordLessLogin(email, emailLoginUrl, useLocalStorage);
            redirectUrl = redirectUrl.replace('{brand}', CsbHelper.getBrandLocal());
            window.location.href = redirectUrl;
        loading?.off();
    }
}
